import { bool, elementType, string } from 'prop-types'
import {
  cloneElement,
  type ComponentType,
  Fragment,
  type FunctionComponent,
  type ReactElement,
  type ReactNode,
} from 'react'

import { type ExperimentName } from 'shared/experiments/consts'
import { type InferPropsWithDefaults } from 'shared/utils/propTypes'
import { type Experiment as ExperimentType } from 'types/shopConfig'
import { usePageProperties } from 'views/utils/shopAndRuntimeConfigUtils'

import {
  compileExperiment,
  getExperimentsList,
  getMatchedVariant,
} from './helpers'

const Experiment: FunctionComponent<ComponentProps> = ({
  children,
  name = '',
  names = [],
  classNames,
  preventActivation,
  component = 'div',
}) => {
  const { experiments = [] } = usePageProperties()
  const Component = component as unknown as ComponentType

  const experimentsNames = getExperimentsList(name, names)
  const experiment = compileExperiment(experimentsNames, experiments)

  let props = {}

  if (component !== Fragment) {
    props = {
      ...props,
      className: classNames,
      'data-experiment': experiment.name,
      'data-experiment-variation': experiment.variant,
      'data-prevent-activation': preventActivation,
    }
  }

  if (typeof children === 'function') {
    return <Component {...props}>{children(experiment)}</Component>
  }

  // We need `as` type casting because TS does not infer
  // its type from the above condition
  const variantNode = getMatchedVariant(experiment, children as ReactElement)

  return (
    <Component {...props}>
      {variantNode
        ? cloneElement(variantNode, {
            variables: experiment.variables,
          })
        : null}
    </Component>
  )
}

const propTypes = {
  classNames: string,
  preventActivation: bool,
  component: elementType,
}

const defaultProps = {
  component: 'div',
}

type ComponentProps = InferPropsWithDefaults<
  typeof propTypes,
  typeof defaultProps
> & {
  children: ((experiment: ExperimentType) => ReactElement) | ReactNode
  name?: ExperimentName
  names?: ExperimentName[]
}

export default Experiment
